import type { CallLogType } from '@kanbu/schema';
import { CallLogEvent } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { cn } from '@utima/ui';
import { format } from 'date-fns';
import { InfoIcon } from 'lucide-react';
import { useState } from 'react';

/**
 * Formats the time difference between two dates into a "+X.Ys" string.
 */
function formatRelativeTime(start: Date, end: Date): string {
  if (
    !(start instanceof Date) ||
    !(end instanceof Date) ||
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime())
  ) {
    return '';
  }

  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 0) {
    return '+0.0s';
  }

  const diffSeconds = (diffMs / 1000).toFixed(1);

  return `+${diffSeconds}s`;
}

/**
 * Gets a Tailwind text color class based on the event type for emphasis.
 */
function getEventTypeColor(eventType: CallLogEvent): string {
  switch (eventType) {
    case CallLogEvent.Error:
      return 'text-red-500';

    case CallLogEvent.CallStarted:
    case CallLogEvent.CallEnded:
      return 'text-purple-500';

    case CallLogEvent.AI:
      return 'text-green-500';

    case CallLogEvent.Log:
      return 'text-gray-500';

    case CallLogEvent.Usage:
      return 'text-amber-500';

    default:
      return 'text-gray-500';
  }
}

export interface CallLogsProps {
  logs: CallLogType[];
}

export function CallLogs({ logs = [] }: CallLogsProps) {
  const [visiblePayloadId, setVisiblePayloadId] = useState<string | null>(null);

  if (!logs || logs.length === 0) {
    return (
      <div className='p-4 text-center'>
        <Trans>No logs available</Trans>
      </div>
    );
  }

  // Process logs only after loading/error checks
  const processedLogs: (Omit<CallLogType, 'timestamp'> & {
    timestamp: Date;
  })[] = logs
    .map(log => ({
      ...log,
      timestamp: new Date(log.timestamp),
    }))
    .filter(log => !Number.isNaN(log.timestamp.getTime()))
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  const startTime = processedLogs[0]?.timestamp;

  if (!startTime) {
    return (
      <div className='p-4 text-center text-sm'>
        Could not determine call start time.
      </div>
    );
  }

  return (
    <div className='w-full overflow-auto rounded-md border bg-background p-2 text-xs text-foreground'>
      {processedLogs.map(log => (
        <div
          key={log.id}
          role='button'
          className='relative grid grid-cols-[60px_55px_100px_1fr] items-start gap-2 rounded border-b border-border py-1 hover:bg-muted/30'
          onClick={() =>
            setVisiblePayloadId(prevId => (prevId === log.id ? null : log.id))
          }
        >
          <span className='pt-px'>{format(log.timestamp, 'HH:mm:ss.SSS')}</span>
          <span className='pt-px text-right text-blue-500'>
            {formatRelativeTime(startTime, log.timestamp)}
          </span>
          <span
            className={cn('font-semibold pt-px', getEventTypeColor(log.event))}
          >
            [{log.event.toUpperCase()}]
          </span>
          <span className='flex items-center gap-1 whitespace-pre-wrap break-words pt-px'>
            {log.message}
            {!!log.payload && (
              <span className='cursor-pointer text-gray-400 hover:text-gray-600'>
                <InfoIcon size={12} />
              </span>
            )}
          </span>
          {!!log.payload && visiblePayloadId === log.id && (
            <div className='col-span-4 w-full whitespace-pre-wrap break-words rounded-radius bg-gray-100 p-1 font-mono'>
              {JSON.stringify(log.payload, null, 2)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
